import { h, createContext } from 'preact';
import { useContext, useEffect, useReducer } from 'preact/hooks';
import Storefront from '../../utils/storefront';
import { KitReducer } from './kit-reducer';
import * as queries from 'utils/storefront/queries';
import { productsByTag } from '../../utils/storefront/queries';
import { formatProduct } from 'utils/storefront/format-resource';

const Context = createContext();

export const useKitProduct = () => useContext(Context);

const calculateInitialState = (data) => {
  return {
    initialKitProducts: data.kitProducts.map((product) => {
      return {
        selectedProduct: product,
        selectedVariant: null,
        groupedProducts: [],
      };
    }),
    kitProducts: null,
  };
};

const KitProductContext = ({ data, children }) => {
  const [state, dispatch] = useReducer(KitReducer, data, calculateInitialState);

  const handleProductChange = (index, lengthTag) => {
    const kitProduct = state.kitProducts[index];

    const newKitProduct = kitProduct.groupedProducts.find(
      (product) => product.lengthTag === lengthTag,
    );

    const newKitVariant = newKitProduct.variants[0];

    const product = {
      ...kitProduct,
      selectedProduct: newKitProduct,
      selectedVariant: newKitVariant,
    };

    dispatch({
      type: 'UPDATE_KIT_PRODUCT',
      payload: {
        updatedProduct: product,
        index,
      },
    });
  };

  const handleColorChange = ({ handle, index }) => {
    const kitProduct = state.kitProducts[index];

    const currentSize = kitProduct.selectedVariant.selectedOptions.find(
      (option) => option.name === 'Size',
    ).value;
    const newKitProduct = kitProduct.groupedProducts.find(
      (p) => p.handle === handle,
    );
    const newKitVariant = newKitProduct.variants.find((v) => {
      const sizeOption = v.selectedOptions.find(
        (option) => option.name === 'Size',
      ).value;
      return sizeOption === currentSize;
    });

    const product = {
      ...kitProduct,
      selectedProduct: newKitProduct,
      selectedVariant: newKitVariant,
    };

    dispatch({
      type: 'UPDATE_KIT_PRODUCT',
      payload: {
        updatedProduct: product,
        index,
      },
    });
  };

  const handleSizeChange = ({ size, index }) => {
    const kitProduct = state.kitProducts[index];
    const newKitVariant = kitProduct.selectedProduct.variants.find((v) => {
      const sizeOption = v.selectedOptions.find(
        (option) => option.name === 'Size',
      ).value;
      return sizeOption === size;
    });

    const product = {
      ...kitProduct,
      selectedVariant: newKitVariant,
    };

    dispatch({
      type: 'UPDATE_KIT_PRODUCT',
      payload: {
        updatedProduct: product,
        index,
      },
    });
  };

  useEffect(async () => {
    const storefront = new Storefront();

    const kitProductData = await Promise.all(
      data.kitProducts.map(async (product, index) => {
        const responseJson = await storefront.request({
          query: productsByTag(data.kitProducts[index].groupTag),
        });
        const filteredResponseJson = responseJson.data.products.nodes;

        let handles = [];

        filteredResponseJson.forEach((element) => {
          handles.push(element.handle);
        });

        const responses = await Promise.all(
          handles.map((handle) => {
            return storefront.request({
              query: queries.productByHandle(),
              variables: {
                handle,
              },
            });
          }),
        );

        const filtered = responses.filter((r) => r.data.productByHandle);

        const colorGroupedProducts = filtered.map((r) => {
          const formatted = formatProduct(r.data.productByHandle);
          const colorName = formatted.variants[0].selectedOptions.find(
            (option) => option.name === 'Color',
          ).value;
          if (Boolean(colorName)) {
            formatted.swatchUrl = `//cdn.shopify.com/s/files/1/1011/2122/files/swatch--${colorName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/-$/, '')
              .replace(/^-/, '')}_100x100.png`;
          }
          formatted.lengthTag = formatted.tags.find((tag) =>
            tag.includes('length'),
          );
          return formatted;
        });

        const initialProduct = colorGroupedProducts.find(
          (p) => p.handle === product.handle,
        );

        return {
          selectedProduct: initialProduct,
          selectedVariant: initialProduct.variants[0],
          groupedProducts: colorGroupedProducts,
        };
      }),
    );

    dispatch({
      type: 'SET_KIT_PRODUCTS',
      payload: kitProductData,
    });
  }, []);

  return (
    <Context.Provider
      value={{
        ...state,
        handleProductChange,
        handleColorChange,
        handleSizeChange,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default KitProductContext;